import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import {
  ApiTimeOffPeriodTrackingTime,
  ApiTimeOffPolicy,
  ApiTimeOffPolicyVersion,
  findCurrentTimeOffPolicyVersion,
  getAccrualFreqKeyMap,
} from "@/types/time-off/time-off-policy.types.ts";
import {
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { subDays } from "date-fns";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

export interface TimeOffPolicyVersionsTimelineProps {
  policy: ApiTimeOffPolicy;
  periodTrackType?: ApiTimeOffPeriodTrackingTime;
  selectedVersionId?: number | null;
  onEditVersion?: (version: ApiTimeOffPolicyVersion) => void;
  onChangeEffectiveDate?: (version: ApiTimeOffPolicyVersion) => void;
}

export function TimeOffPolicyVersionsTimeline({
  policy,
  periodTrackType,
  selectedVersionId,
  onEditVersion,
  onChangeEffectiveDate,
}: TimeOffPolicyVersionsTimelineProps) {
  const { t } = useTranslation();
  const { renderDate, renderEmployeeName } = useFormatter();
  const [expanded, setExpanded] = useState(false);
  const isHours = periodTrackType === ApiTimeOffPeriodTrackingTime.hours;
  const accrualFreqKeyMap = getAccrualFreqKeyMap();

  const buildMilestoneCaption = (version: ApiTimeOffPolicyVersion): string => {
    return version.milestones
      .map((m) => {
        const amount = Number(m.accrual_amount);
        if (Number.isNaN(amount)) return null;
        const unit = isHours
          ? t("plurals:plural_hours", { count: amount })
          : t("plurals:plural_day", { count: amount });
        const freq = accrualFreqKeyMap[m.accrual_frequency_type];
        return `${unit} ${freq}`;
      })
      .filter(Boolean)
      .join(", ");
  };

  const formatDate = (value: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return renderDate(parsed);
  };

  const formatDateMinusOne = (value: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return renderDate(subDays(parsed, 1));
  };

  const versions = policy.versions;
  if (versions.length <= 1) return null;

  const actualCurrentVersion = findCurrentTimeOffPolicyVersion(versions);
  const selectedVersion =
    (selectedVersionId != null ? versions.find((v) => v.id === selectedVersionId) : null) ??
    actualCurrentVersion ??
    versions[0];
  const historicalChangesCount = versions.length;
  const todayMs = new Date().setHours(0, 0, 0, 0);

  const currentDate = formatDate(selectedVersion.date_effective_from);
  const headerTitle = currentDate
    ? t("time_off:current_rules_since", { date: currentDate })
    : t("time_off:initial_rules");

  return (
    <Stack gap={"md"}>
      <ListItem
        icon={
          <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-clock-rotate-left"} />} size={"md"} />
        }
        title={headerTitle}
        caption={t("plurals:historical_changes_count", { count: historicalChangesCount })}
        valueSlot={
          <Button
            variant={"secondary"}
            rightIcon={
              <FontAwesomeIcon
                icon={expanded ? "fa-light fa-chevron-down" : "fa-light fa-chevron-right"}
              />
            }
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? t("time_off:hide_changes") : t("time_off:view_changes")}
          </Button>
        }
      />

      {expanded && (
        <Box variant={"border"}>
          <Title
            header={t("time_off:changes_timeline")}
            size={"xs"}
            variant={"secondary"}
            paddingBottom
          />
          <Stack gap={"xs"}>
            {versions.map((version, index) => {
              const isCurrent = version.id === selectedVersion.id;
              const isInitial = !version.date_effective_from;
              const fromMs = version.date_effective_from
                ? new Date(version.date_effective_from).getTime()
                : null;
              const isFuture = fromMs !== null && !Number.isNaN(fromMs) && fromMs > todayMs;
              const isEditable = version.id === actualCurrentVersion?.id || isFuture;
              const fromDate = formatDate(version.date_effective_from);
              const nextVersion = versions[index - 1];
              const nextDate = nextVersion
                ? formatDateMinusOne(nextVersion.date_effective_from)
                : null;
              let rangeText: string;
              if (fromDate && nextDate) {
                rangeText = `${fromDate} — ${nextDate}`;
              } else if (fromDate) {
                rangeText = t("time_off:since_date", { date: fromDate });
              } else if (isInitial && nextDate) {
                rangeText = `${t("time_off:initial_rules")} · ${t("time_off:before_date", { date: nextDate })}`;
              } else {
                rangeText = t("time_off:initial_rules");
              }

              const milestoneSummary = buildMilestoneCaption(version);
              const authorName = version.created_by ? renderEmployeeName(version.created_by) : null;
              const caption = [authorName, milestoneSummary || null].filter(Boolean).join(" · ");

              return (
                <Fragment key={version.id}>
                  {index > 0 && (
                    <div className={"w-9 h-5 flex justify-center"}>
                      <div className={"w-px h-full bg-dark/20"} />
                    </div>
                  )}
                  <ListItem
                    behavior={"card"}
                    icon={
                      version.created_by ? (
                        <EmployeeAvatar employee={version.created_by} size={"md"} />
                      ) : (
                        <Avatar
                          icon={<FontAwesomeIcon icon={"fa-light fa-user-circle"} />}
                          size={"md"}
                        />
                      )
                    }
                    title={rangeText}
                    caption={caption || undefined}
                    valueSlot={
                      <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                        {isCurrent && (
                          <Text className={"text-secondary"}>{t("time_off:current_rules")}</Text>
                        )}
                        {isEditable && (
                          <DropdownMenu
                            trigger={
                              <Button
                                variant={"secondary"}
                                rightIcon={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} />}
                              />
                            }
                          >
                            <DropdownMenuItem
                              title={t("time_off:edit_rules")}
                              onClick={() => onEditVersion?.(version)}
                            />
                            {isFuture && (
                              <DropdownMenuItem
                                title={t("time_off:change_effective_date")}
                                onClick={() => onChangeEffectiveDate?.(version)}
                              />
                            )}
                          </DropdownMenu>
                        )}
                      </Stack>
                    }
                  />
                </Fragment>
              );
            })}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
